import rtti from '@/sui/tools/rtti'
import assert from '@/sui/tools/assert'
import checker from '@/sui/tools/checker'

import tools from '@/sui/data/sheets/edit/tools'

import clone from 'clone'

function goFieldToList (field, options) {
  assert.notNull(field.cfg)
  assert.isString(field.cfg.title)
  assert.isArray(field.cfg.values)

  return tools.cfg.list(
    field.cfield, field.cfg.title, field.cfg.values, options
  )
}

function goFieldToPickList (field, options) {
  assert.notNull(field.cfg)
  assert.isString(field.cfg.title)
  assert.isArray(field.cfg.values)

  return tools.cfg.pickList(
    field.cfield,
    field.cfg.title,
    field.cfg.values,
    options
  )
}

function goFieldToDate (field, options) {
  assert.notNull(field.cfg)
  assert.isString(field.cfg.context)

  return tools.cfg.date(
    field.cfield, field.cfg.context, options
  )
}

function goFieldToNumeric (field, options) {
  assert.notNull(field.cfg)
  assert.isNumber(field.cfg.digits)
  assert.isString(field.cfg.title)

  return tools.cfg.numeric(
    field.cfield, field.cfg.title, field.cfg.digits, options
  )
}

function goFieldToText (field, options) {
  assert.notNull(field.cfg)
  assert.isString(field.cfg.title)

  return tools.cfg.text(
    field.cfield, field.cfg.title, options
  )
}

function goFieldToBool (field, options) {
  assert.notNull(field.cfg)
  assert.isString(field.cfg.title)

  return tools.cfg.bool(
    field.cfield, field.cfg.title, options
  )
}

function goFieldToArray (field, options, stack) {
  assert.notNull(field.cfg)
  assert.isString(field.cfg.title)
  assert.isObject(field.cfg.fields, 'cfg.fields is not an array')
  assert.isArray(stack, 'Stack is not an array')

  const result = {
    field: field.cfield,
    type: 'array',
    fields: {},
    config: {
      title: field.cfg.title,
      exclusive: field.cfg.exclusive
    },
    convert: {
      server: options.srv
    }
  }
  stack.push({ target: result.fields, list: field.cfg.fields })
  return result
}

function goFieldToJS (field, stack) {
  assert.isObject(field)
  assert.isString(field.type)
  assert.isString(field.cfield)
  assert.isString(field.sfield)

  const options = {
    srv: field.sfield !== field.cfield ? field.sfield : null
  }

  const convert = {
    list: goFieldToList,
    bool: goFieldToBool,
    pickList: goFieldToPickList,
    date: goFieldToDate,
    numeric: goFieldToNumeric,
    text: goFieldToText,
    array: goFieldToArray
  }
  const func = convert[field.type]
  assert.notNull(func)
  return func(field, options, stack)
}

export function goFieldsToJS (fields, ignoreList = []) {
  assert.isObject(fields)
  assert.isArray(ignoreList)

  const result = {}
  const stack = [{ target: result, list: fields }]
  let i = 0
  let valid = false
  while (i !== stack.length) {
    const current = stack[i]

    for (let key in current.list) {
      const field = current.list[key]
      if (ignoreList.indexOf(key) !== -1) {
        continue
      }

      current.target[key] = goFieldToJS(
        field,
        stack
      )
      valid = true
    }
    ++i
  }
  return valid ? result : null
}

function buildSelector (selector) {
  const rootTarget = []
  const stack = [{ source: selector, target: rootTarget }]
  while (stack.length > 0) {
    const current = stack[stack.length - 1]
    assert.isString(current.source.func)

    let leaf = true
    const initial = current.target.length
    for (let i = initial; i !== current.source.args.length; ++i) {
      const arg = current.source.args[i]
      if (rtti.isObject(arg)) {
        leaf = false
        stack.push({ source: arg, target: [] })
        break
      }
      current.target.push(arg)
    }

    if (leaf) {
      stack.pop()
      if (stack.length > 0) {
        const parent = stack[stack.length - 1]
        const leaf = tools[current.source.func](...current.target)
        parent.target.push(
          leaf
        )
      }
    }
  }
  assert.notNull(tools[selector.func], `Invalid selector function : ${selector.func}`)
  const result = tools[selector.func](...rootTarget)
  return result
}

function removeExistingValues (values, paths, field) {
  assert.isArray(values)
  assert(values.length > 0)
  assert.isArray(paths)
  assert(paths.length > 0)

  function collectValues (ctx, items) {
    const result = new Set()
    let found = false
    for (let i = 0; i !== items.length; ++i) {
      const item = items[i]
      if (item === ctx) {
        found = true
        continue
      }

      const mainField = item[field]
      if (mainField != null) {
        result.add(mainField)
      }
    }

    if (!found) {
      return null
    }
    return result
  }

  const entryPoint = paths[0]
  function getter (ctx, sheet) {
    assert.notNull(ctx)
    assert.notNull(sheet)

    const start = sheet[entryPoint]
    if (start == null || start.length < 1) {
      return null
    }

    const stack = [{ target: start }]
    while (stack.length > 0) {
      const current = stack[stack.length - 1]
      assert(current.target != null && current.target.length >= 1)

      if (stack.length === paths.length) {
        const values = collectValues(ctx, current.target)
        if (values != null) {
          return values
        }
        stack.pop()
        continue
      }

      current.index = current.index == null ? 0 : current.index + 1
      if (current.index === current.target.length) {
        stack.pop()
        continue
      }

      const parent = current.target[current.index]
      const path = paths[stack.length]
      const nextTarget = parent[path]
      if (nextTarget == null || nextTarget.length < 1) {
        stack.pop()
        continue
      }

      stack.push({ target: nextTarget })
    }
    return null
  }

  return function (ctx, sheet) {
    const sheetValues = getter(ctx, sheet)
    if (sheetValues == null || sheetValues.length === 0) {
      return {}
    }

    const remainingValues = []
    for (let i = 0; i !== values.length; ++i) {
      const value = values[i]
      const index = value.index != null ? value.index : value.code
      if (!sheetValues.has(index)) {
        remainingValues.push(value)
      }
    }
    return {
      values: remainingValues
    }
  }
}

function getStackPath (stack) {
  assert.isArray(stack)
  const result = []
  for (let k = 0; k !== stack.length; ++k) {
    const s = stack[k]
    if (s.field != null) {
      result.push(s.field)
    }
  }
  return result
}

export function goTreeToJS (tree, fields) {
  const popValue = -1
  const result = []

  const stack = [{ source: tree, target: result, fields: fields }]
  while (stack.length > 0) {
    const current = stack[stack.length - 1]

    let complete = true
    for (let i = current.pos || 0; i !== current.source.length; ++i) {
      const item = current.source[i]
      if (item.type === 'field') {
        let field = current.fields[item.field]
        assert.notNull(field, `Invalid field '${item.field}'`)
        if (current.pop) {
          if (current.mainField == null) {
            current.mainField = item.field
            const path = getStackPath(stack)

            const values = [
              { code: popValue, value: '(Aucun)' },
              ...field.config.values
            ]
            field = tools.opts(
              field, {
                pop: tools.oneTrue(
                  tools.isNull(current.mainField),
                  tools.equals(current.mainField, popValue)
                ),
                ctx: current.exclusive
                  ? removeExistingValues(values, path, field.field) : null
              }
            )
            field.config.values = values
          } else {
            field = tools.andSelect(
              field,
              tools.different(current.mainField, popValue)
            )
          }
        }

        if (current.selector) {
          field = tools.andSelect(field, current.selector)
        }

        field.config.csvField = item.csvField
        current.target.push(field)
        continue
      }

      if (item.type === 'array') {
        const source = current.fields[item.field]
        source.config.csvArrayPrefix = item.csvArrayPrefix
        assert.notNull(source, `Invalid field '${item.field}'`)
        let field = clone(source)
        field.fields = null
        field.config.fields = []
        if (current.selector) {
          field = tools.andSelect(field, current.selector)
        }
        current.target.push(field)
        stack.push({
          source: item.sequence,
          target: field.config.fields,
          fields: source.fields,
          pop: true,
          mainField: null,
          field: field.field,
          exclusive: field.config.exclusive
        })
        current.pos = i + 1
        complete = false
        break
      }

      if (item.type === 'if') {
        let selector = buildSelector(item.selector)
        if (current.selector) {
          selector = tools.allTrue(current.selector, selector)
        }

        stack.push({
          source: item.fields,
          target: current.target,
          fields: current.fields,
          selector: selector
        })
        current.pos = i + 1
        complete = false
        break
      }
    }
    if (complete) {
      stack.pop()
    }
  }
  return result
}

const debug = process.env.VUE_APP_SPIADI_DEBUG === '1'
if (debug) {
  const selectorTests = [
    // notNull
    {
      selector: { func: 'notNull', args: ['field'] },
      result: 'notNull (field)'
    },

    // isNull
    {
      selector: { func: 'isNull', args: ['field'] },
      result: 'isNull (field)'
    },

    // match
    {
      selector: { func: 'match', args: ['field', 'A|B'] },
      result: '(field match "A|B")'
    },

    // equals
    {
      selector: { func: 'equals', args: ['field', 4] },
      result: '(field === 4)'
    },
    {
      selector: { func: 'equals', args: ['field', 'str'] },
      result: '(field === "str")'
    },

    // equals2
    {
      selector: { func: 'equals2', args: ['field', 4, 5] },
      result: '(field === 4 || field === 5)'
    },
    {
      selector: { func: 'equals2', args: ['field', 'str', 'str2'] },
      result: '(field === "str" || field === "str2")'
    },

    // different
    {
      selector: { func: 'different', args: ['field', 4] },
      result: '(field != null && field !== 4)'
    },
    {
      selector: { func: 'different', args: ['field', 'str'] },
      result: '(field != null && field !== "str")'
    },

    // different2
    {
      selector: { func: 'different2', args: ['field', 4, 5] },
      result: '(field != null && field !== 4 && field !== 5)'
    },
    {
      selector: { func: 'different2', args: ['field', 'str', 'str2'] },
      result: '(field != null && field !== "str" && field !== "str2")'
    },

    // inList
    {
      selector: {
        func: 'inList',
        args: ['field', [3, 12]]
      },

      result: '(field in [3, 12])'
    },
    {
      selector: {
        func: 'inList',
        args: ['field', ['str1', 'str2']]
      },

      result: '(field in ["str1", "str2"])'
    },

    // surOrEqual
    {
      selector: { func: 'supOrEqual', args: ['field', 12] },
      result: '(field != null && field >= 12)'
    },

    // inf
    {
      selector: { func: 'inf', args: ['field', 8] },
      result: '(field != null && field < 8)'
    },

    // not
    {
      selector: {
        func: 'not',
        args: [
          { func: 'match', args: ['field', 'A|C'] }
        ]
      },
      result: 'not ((field match "A|C"))'
    },

    // oneTrue + allTrue
    {
      selector: {
        func: 'oneTrue',
        args: [
          {
            func: 'equals',
            args: [ 'type', 2 ]
          },
          {
            func: 'allTrue',
            args: [
              {
                func: 'different',
                args: ['type', 3]
              },
              {
                func: 'equals2',
                args: ['unitType', 22, 13]
              }
            ]
          }
        ]
      },
      result: 'oneTrue ((type === 2), allTrue ((type != null && type !== 3), (unitType === 22 || unitType === 13)))'
    },
    {
      selector: {
        func: 'not',
        args: [
          {
            func: 'oneTrue',
            args: [
              {
                func: 'match',
                args: ['type', 'C2|C3']
              },
              {
                func: 'inList',
                args: ['unitType', ['AD REA MED', 'AD REA NEU']]
              },
              {
                func: 'supOrEqual',
                args: ['bmi', 30]
              },
              {
                func: 'notNull',
                args: ['uf']
              },
              {
                func: 'isNull',
                args: ['sheetNumber']
              }
            ]
          }
        ]
      },
      result: 'not (oneTrue ((type match "C2|C3"), (unitType in ["AD REA MED", "AD REA NEU"]), (bmi != null && bmi >= 30), notNull (uf), isNull (sheetNumber)))'
    }
  ]

  const c = checker('fromGo')
  for (let i = 0; i !== selectorTests.length; ++i) {
    const test = selectorTests[i]
    const resultTest = buildSelector(test.selector)
    c.check(
      resultTest.name === test.result,
      `Unexpected selector : "${resultTest.name}", expected "${test.result}"`
    )
  }
  c.end()
}
